import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  Alert,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import { authAPI } from '../../services/api';

const createSchema = yup.object({
  firstName: yup.string().required('First name is required').max(50, 'First name must be less than 50 characters'),
  lastName: yup.string().required('Last name is required').max(50, 'Last name must be less than 50 characters'),
  email: yup.string().email('Invalid email format').required('Email is required').max(100, 'Email must be less than 100 characters'),
  password: yup.string().required('Password is required').min(6, 'Password must be at least 6 characters'),
  confirmPassword: yup.string()
    .required('Please confirm your password')
    .oneOf([yup.ref('password')], 'Passwords must match'),
  role: yup.string().required('Role is required').oneOf(['admin', 'manager', 'user'], 'Invalid role'),
  department: yup.string().max(50, 'Department must be less than 50 characters'),
  phone: yup.string().max(20, 'Phone must be less than 20 characters'),
  isActive: yup.boolean()
});

const editSchema = yup.object({
  firstName: yup.string().required('First name is required').max(50, 'First name must be less than 50 characters'),
  lastName: yup.string().required('Last name is required').max(50, 'Last name must be less than 50 characters'),
  email: yup.string().email('Invalid email format').required('Email is required').max(100, 'Email must be less than 100 characters'),
  password: yup.string().min(6, 'Password must be at least 6 characters'),
  confirmPassword: yup.string()
    .test('passwords-match', 'Passwords must match', function(value) {
      const { password } = this.parent;
      if (password && value && password !== value) {
        return false;
      }
      return true;
    }),
  role: yup.string().required('Role is required').oneOf(['admin', 'manager', 'user'], 'Invalid role'),
  department: yup.string().max(50, 'Department must be less than 50 characters'),
  phone: yup.string().max(20, 'Phone must be less than 20 characters'),
  isActive: yup.boolean()
});

const UserForm = ({ open, onClose, onSuccess, user }) => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const isEdit = !!user;
  const schema = isEdit ? editSchema : createSchema;

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      role: 'user',
      department: '',
      phone: '',
      isActive: true
    }
  });

  const watchedPassword = watch('password');

  useEffect(() => {
    if (open) {
      if (isEdit && user) {
        reset({
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          email: user.email || '',
          password: '',
          confirmPassword: '',
          role: user.role || 'user',
          department: user.department || '',
          phone: user.phone || '',
          isActive: user.isActive !== undefined ? user.isActive : true
        });
      } else {
        reset({
          firstName: '',
          lastName: '',
          email: '',
          password: '',
          confirmPassword: '',
          role: 'user',
          department: '',
          phone: '',
          isActive: true
        });
      }
    }
  }, [open, isEdit, user, reset]);

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      
      // Remove confirmPassword from data
      const { confirmPassword, ...submitData } = data;
      
      // If editing and password is empty, remove it from data
      if (isEdit && !submitData.password) {
        delete submitData.password;
      }
      
      if (isEdit) {
        await authAPI.updateUser(user.id, submitData);
        toast.success('User updated successfully');
      } else {
        await authAPI.register(submitData);
        toast.success('User created successfully');
      }
      
      onSuccess();
    } catch (error) {
      console.error('Error saving user:', error);
      const message = error.response?.data?.message || `Failed to ${isEdit ? 'update' : 'create'} user`;
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '500px' }
      }}
    >
      <DialogTitle>
        <Typography variant="h5" fontWeight={600}>
          {isEdit ? 'Edit User' : 'Add New User'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {isEdit ? 'Update user information and permissions' : 'Create a new user account'}
        </Typography>
      </DialogTitle>
      
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Grid container spacing={3}>
            {/* Personal Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
                Personal Information
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Controller
                name="firstName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="First Name"
                    fullWidth
                    error={!!errors.firstName}
                    helperText={errors.firstName?.message}
                    placeholder="Enter first name"
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Controller
                name="lastName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Last Name"
                    fullWidth
                    error={!!errors.lastName}
                    helperText={errors.lastName?.message}
                    placeholder="Enter last name"
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Email"
                    type="email"
                    fullWidth
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    placeholder="Enter email address"
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Phone (Optional)"
                    fullWidth
                    error={!!errors.phone}
                    helperText={errors.phone?.message}
                    placeholder="Enter phone number"
                  />
                )}
              />
            </Grid>

            {/* Account Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Account Information
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Controller
                name="role"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.role}>
                    <InputLabel>Role</InputLabel>
                    <Select {...field} label="Role">
                      <MenuItem value="user">User</MenuItem>
                      <MenuItem value="manager">Manager</MenuItem>
                      <MenuItem value="admin">Administrator</MenuItem>
                    </Select>
                    {errors.role && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                        {errors.role.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Controller
                name="department"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Department (Optional)"
                    fullWidth
                    error={!!errors.department}
                    helperText={errors.department?.message}
                    placeholder="Enter department"
                  />
                )}
              />
            </Grid>

            {/* Password Section */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                {isEdit ? 'Change Password (Optional)' : 'Password'}
              </Typography>
              {isEdit && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Leave password fields empty to keep the current password.
                </Alert>
              )}
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={isEdit ? "New Password" : "Password"}
                    type={showPassword ? 'text' : 'password'}
                    fullWidth
                    error={!!errors.password}
                    helperText={errors.password?.message}
                    placeholder={isEdit ? "Enter new password" : "Enter password"}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={togglePasswordVisibility}
                            edge="end"
                            size="small"
                          >
                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Controller
                name="confirmPassword"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Confirm Password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    fullWidth
                    error={!!errors.confirmPassword}
                    helperText={errors.confirmPassword?.message}
                    placeholder="Confirm password"
                    disabled={isEdit && !watchedPassword}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={toggleConfirmPasswordVisibility}
                            edge="end"
                            size="small"
                            disabled={isEdit && !watchedPassword}
                          >
                            {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            {/* Status */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Status
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch {...field} checked={field.value} />}
                    label="Active User"
                  />
                )}
              />
              <Typography variant="caption" color="text.secondary" display="block">
                Inactive users cannot log in to the system
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={handleClose}
            disabled={loading}
            startIcon={<CancelIcon />}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={<SaveIcon />}
          >
            {loading ? 'Saving...' : (isEdit ? 'Update User' : 'Create User')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UserForm;